// src/components/GeminiApiKeyInput.js
import React, { useState, useEffect } from 'react';
import { Key, Eye, EyeOff, Save } from 'lucide-react';

const GeminiApiKeyInput = ({ onApiKeyChange }) => {
    const [apiKey, setApiKey] = useState('');
    const [showKey, setShowKey] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        // Load API key from localStorage on mount
        const storedKey = localStorage.getItem('gemini_api_key') || '';
        if (storedKey) {
            setApiKey(storedKey);
            onApiKeyChange(storedKey);
        }
    }, [onApiKeyChange]);

    const handleSaveKey = () => {
        if (apiKey.trim()) {
            localStorage.setItem('gemini_api_key', apiKey.trim());
            onApiKeyChange(apiKey.trim());
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        }
    };

    const handleKeyChange = (e) => {
        setApiKey(e.target.value);
    };

    return (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 shadow-xl backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-3">
                <Key size={20} className="text-indigo-400" />
                <h3 className="text-lg font-semibold text-white">Google Gemini API Key</h3>
            </div>

            <p className="text-sm text-slate-400 mb-4">
                Enter your Google Gemini API key to use this tool. Your key is stored locally in your browser.
            </p>

            <div className="flex gap-2">
                <div className="flex-1 relative">
                    <input
                        type={showKey ? 'text' : 'password'}
                        value={apiKey}
                        onChange={handleKeyChange}
                        placeholder="Enter your Gemini API key..."
                        className="w-full p-3 pr-12 bg-slate-900/50 border border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200 font-mono text-sm"
                    />
                    <button
                        onClick={() => setShowKey(!showKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
                        title={showKey ? 'Hide key' : 'Show key'}
                    >
                        {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                </div>

                <button
                    onClick={handleSaveKey}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${saved
                            ? 'bg-green-600 text-white'
                            : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                        }`}
                >
                    <Save size={18} />
                    {saved ? 'Saved!' : 'Save'}
                </button>
            </div>

            <p className="text-xs text-slate-500 mt-3">
                Get your API key from{' '}
                <a
                    href="https://aistudio.google.com/app/apikey"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:text-indigo-300 underline"
                >
                    Google AI Studio
                </a>
            </p>
        </div>
    );
};

export default GeminiApiKeyInput;
